import React, { Fragment } from 'react'
import styled, { css } from 'styled-components'
import { func, bool, string, object, arrayOf } from 'prop-types'

import Form from './Form'
import Text from './Text'
import Input from './Input'
import Button from './Button'
import Dropdown from './Dropdown'

import { WHITE, SILVER, SHADOW_GREY, ORANGE } from '../../constants'
import { SECONDARY_BUTTON } from '../../constants/buttonThemes'
import { newMappingRowSchema } from '../../formValidationSchemas'
import { dropdownValueShape } from '../../types'
import capitalizeFirstLetter from '../../lib/capitalizeFirstLetter'

const MappingKeyField = styled.div`
  ${({ variant }) => keyVariants[variant]};
`

const MappingValueField = styled.div`
  ${({ variant }) => valueVariants[variant]};
`

const keyVariants = {
  master: css`
    display: flex;
    justify-content: center;
    flex-direction: column;
    overflow: hidden;
    flex: 0 0 33%;
  `,
  partner: css`
    display: flex;
    justify-content: center;
    flex-direction: column;
    overflow: hidden;
    flex: 0 0 25%;
  `,
  outgoing: css`
    display: flex;
    justify-content: center;
    flex-direction: column;
    overflow: hidden;
    flex: 0 0 50%;
  `,
}

const valueVariants = {
  master: css`
    display: flex;
    flex: 0 0 33%;
  `,
  partner: css`
    display: flex;
    flex: 0 0 25%;
  `,
  outgoing: css`
    display: flex;
    width: 100%;
  `,
}

const FormContentWrapper = styled.div`
  display: flex;
  background: ${WHITE};
  padding: 10px;
  align-items: center;
  box-shadow: ${SHADOW_GREY};
  gap: 10px;
  border-bottom: 1px solid ${SILVER};
  border-top: 1px solid transparent;
  border-left: 1px solid transparent;
  border-right: 1px solid transparent;
`

const ColumnTypeField = styled.div`
  flex: 0 0 25%;
`

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-left: auto;
  flex: 0 0 auto;
  gap: 5px;
`

const MappingEditable = ({
  columnType,
  columnTypeKeys,
  dispatch,
  isAddPartner,
  isOutgoing,
  keys,
  mapping,
  mappingField,
  name,
  setFormVisible,
  t,
  updateMapping,
}) => {
  const variant = isAddPartner ? 'partner' : isOutgoing ? 'outgoing' : 'master'

  return (
    <Form
      initialValues={{
        name: name || '',
        mappingField: mappingField || '',
        mappingRequired: columnType || '',
      }}
      onSubmit={(values, { resetForm }) => {
        const { name, mappingField, mappingRequired } = values
        Promise.resolve(
          updateMapping(
            name,
            mappingField || '',
            mapping,
            dispatch,
            mappingRequired
          )
        ).then(() => {
          resetForm()
          setFormVisible(false)
        })
      }}
      validationSchema={newMappingRowSchema}
      content={(_, values) => (
        <Fragment>
          <FormContentWrapper isAddPartner={isAddPartner}>
            <MappingKeyField variant={variant}>
              {name && <Text bold>{capitalizeFirstLetter(name)}</Text>}
              {!name && (
                <Input
                  name="name"
                  data-id="new-row"
                  placeholder={t('input_placeholder_column_name')}
                />
              )}
            </MappingKeyField>
            <MappingValueField variant={variant}>
              <Dropdown
                name="mappingField"
                dataId="mapping-field-dropdown"
                options={[{ id: '', name: t('none') }, ...keys]}
                placeholder={mappingField || t('none')}
                value={{
                  id: values.mappingField,
                  name: values.mappingField,
                }}
              />
            </MappingValueField>
            {columnTypeKeys && (
              <ColumnTypeField>
                <Dropdown
                  name="mappingRequired"
                  dataId="mapping-required-dropdown"
                  options={columnTypeKeys}
                  placeholder={columnType || columnTypeKeys[0].name}
                  value={{
                    id: values.mappingRequired,
                    name: values.mappingRequired,
                  }}
                />
              </ColumnTypeField>
            )}
            <ButtonsContainer>
              <Button
                theme={SECONDARY_BUTTON}
                type="submit"
                dataId="save-mapping"
              >
                {t('button_save')}
              </Button>
              <Button
                theme={SECONDARY_BUTTON}
                onClick={() => setFormVisible(false)}
                dataId="cancel-mapping"
              >
                {t('button_cancel')}
              </Button>
            </ButtonsContainer>
          </FormContentWrapper>
        </Fragment>
      )}
    />
  )
}

MappingEditable.propTypes = {
  columnType: string,
  columnTypeKeys: arrayOf(dropdownValueShape),
  isAddPartner: bool,
  isOutgoing: bool,
  keys: arrayOf(dropdownValueShape).isRequired,
  mapping: object.isRequired,
  mappingField: string,
  name: string,
  setFormVisible: func,
  updateMapping: func,
}

export default MappingEditable
